import PropTypes from 'prop-types';
import { forwardRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';
import { MENU_OPEN, SET_MENU } from 'store/actions';
import { secureSessionStorage } from 'store/SecureStorage';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

const NavItem = ({ item, level, isCollapsed }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const customization = useSelector((state) => state.customization);
    const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));

    const isSelected = customization.isOpen.findIndex((id) => id === item?.id) > -1;

    const itemIcon = (
        <FiberManualRecordIcon
            sx={{
                width: 8,
                height: 8,
                color: '#fff',
            }}
            fontSize={level > 0 ? 'inherit' : 'medium'}
        />
    );
    let listItemProps = {
        component: forwardRef((props, ref) => (
            <Link ref={ref} {...props} to={item.url} target={item.target ? '_blank' : '_self'} />
        )),
    };
    if (item?.external) {
        listItemProps = { component: 'a', href: item.url, target: '_blank' };
    }

    const itemHandler = (id) => {
        dispatch({ type: MENU_OPEN, id });
        if (matchesSM) dispatch({ type: SET_MENU, opened: false });
        secureSessionStorage.set('_fni', id);
    };

    useEffect(() => {
        const currentIndex = document.location.pathname
            .toString()
            .split('/')
            .findIndex((id) => id === item.id);
        if (currentIndex > -1) dispatch({ type: MENU_OPEN, id: item.id });
        // eslint-disable-next-line
    }, []);

    return (
        <ListItemButton
            {...listItemProps}
            disabled={item.disabled}
            selected={isSelected}
            onClick={() => itemHandler(item.id)}
            sx={{
                mb: 0.5,
                borderRadius: '5px',
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                px: isCollapsed ? 1.5 : 2,
                py: 1,
                minHeight: 40,
                position: 'relative',
                backgroundColor: 'transparent',
                color: '#fff',
                '&.Mui-selected': {
                    backgroundColor: '#b71c1c',
                    color: '#fff',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0, top: 0, bottom: 0,
                        width: 3,
                        backgroundColor: '#fff',
                    },
                    '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.16)',
                    },
                },
                '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.07)',
                },
                transition: 'background 0.15s ease',
            }}
        >
            {/* Icon */}
            <ListItemIcon
                sx={{
                    my: 'auto',
                    minWidth: 0,
                    mr: isCollapsed ? 0 : 1.5,
                    color: '#fff',
                    justifyContent: 'center',
                    opacity: isSelected ? 1 : 0.75,
                    transition: 'opacity 0.15s',
                }}
            >
                {itemIcon}
            </ListItemIcon>

            {/* Label */}
            {!isCollapsed && (
                <ListItemText
                    primary={
                        <Typography
                            sx={{
                                fontSize: '0.83rem',
                                fontWeight: isSelected ? 700 : 400,
                                color: '#fff',
                                lineHeight: 1.3,
                                transition: 'font-weight 0.15s',
                            }}
                        >
                            {item.title}
                        </Typography>
                    }
                />
            )}

        </ListItemButton>
    );
};

NavItem.propTypes = {
    item: PropTypes.object,
    level: PropTypes.number,
    isCollapsed: PropTypes.bool,
};

export default NavItem;